/**
 * Haptic feedback via the Vibration API (`navigator.vibrate`), used to give
 * sliders and chart scrubbing a physical tick as the value steps.
 *
 * Behind the `haptic-feedback` feature flag. Support is also partial: Chrome
 * and Android WebView vibrate, while Safari and iOS WebKit expose no Vibration
 * API at all, so calls there are silently no-ops.
 */

import { hasFeatureEnabled } from '../feature';

/**
 * Durations in ms. The Vibration API exposes no amplitude control, so pulse
 * length is the only lever for intensity: these are near the shortest a motor
 * can render, giving the faintest tap the hardware allows.
 */
const HAPTIC_DURATIONS_MS = {
  /** One step of a slider or one point of a scrub. */
  tick: 1,
  /** Start of a gesture, or a committed value. */
  selection: 3,
} as const;

export type HapticPattern = keyof typeof HAPTIC_DURATIONS_MS;

/**
 * Dragging can cross many steps a second. Vibrations that overlap merge into a
 * continuous buzz, so ticks closer together than this are dropped. A wide gap
 * keeps a fast drag feeling light rather than constant.
 */
const MIN_INTERVAL_MS = 80;

let lastVibrationAt = 0;

const canVibrate = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

/**
 * Vibrates the device if the feature is on and the browser supports it.
 * Returns whether a vibration was requested, which callers can ignore:
 * feedback is decorative.
 */
export const triggerHaptic = (pattern: HapticPattern = 'tick'): boolean => {
  if (!hasFeatureEnabled('haptic-feedback') || !canVibrate()) return false;

  const now = Date.now();
  if (now - lastVibrationAt < MIN_INTERVAL_MS) return false;
  lastVibrationAt = now;

  return navigator.vibrate(HAPTIC_DURATIONS_MS[pattern]);
};

/** Test-only: forget the throttle window between cases. */
export const resetHapticThrottle = (): void => {
  lastVibrationAt = 0;
};
